import type { FunctionComponent } from "react";
import { RiArrowUpLine, RiMailLine, RiPhoneLine, RiMapPinLine } from "@remixicon/react";
import logoBlanco from "../../../assets/img/logo-pj-blanco-footer.svg";
import JusticiaAlDiaSvg from "../../../assets/img/justicia-al-dia.svg";

interface CompactFooterProps {
    showScrollButton?: boolean;
    showLogo?: boolean;
    variant?: 'minimal' | 'standard' | 'full';
}

const CompactFooter: FunctionComponent<CompactFooterProps> = ({
    showScrollButton = true,
    showLogo = true,
    variant = 'minimal'
}) => {
    const handleScrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderMinimal = () => (
        <div className="flex py-3 px-6 justify-center">
            <div className="flex flex-col sm:flex-row w-full justify-between items-center gap-4 text-white">
                <div className="hidden sm:flex w-full items-center gap-3 text-sm justify-start">
                    {showLogo && (
                        <img
                            src={JusticiaAlDiaSvg}
                            alt="Justicia al Día"
                            className="h-8 w-auto"
                        />
                    )}
                </div>
                <div className="flex items-center gap-3 text-sm w-full justify-center">
                    <span className="font-medium text-xs text-gray-300">© 2025 Poder Judicial República Dominicana</span>
                    <span className="sm:flex hidden text-gray-300">|</span>
                    <span className="sm:flex hidden text-xs text-gray-300">Todos los derechos reservados</span>
                </div>
                <div className="flex items-center gap-4 w-full">
                    {/* <a 
                        href="mailto:contacto@poderjudicial.gob.do" 
                        className="text-gray-300 hover:text-white transition-colors text-sm flex items-center gap-1"
                    >
                        <RiMailLine size={14} />
                        contacto@poderjudicial.gob.do
                    </a> */}
                </div>
            </div>
        </div>
    );

    const renderStandard = () => (
        <div className="py-6 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start gap-6 mb-4">
                    {showLogo && (
                        <div className="flex items-center gap-4">
                            <img
                                src={logoBlanco}
                                alt="Poder Judicial"
                                className="h-16 w-auto"
                            />
                            <img
                                src={JusticiaAlDiaSvg}
                                alt="Justicia al Día"
                                className="h-12 w-auto"
                            />
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row gap-6 text-white text-sm">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-start gap-2">
                                <RiMapPinLine size={16} className="mt-0.5 text-gray-300" />
                                <div>
                                    <p className="font-medium">Av. Enrique Jiménez Moya</p>
                                    <p className="text-gray-300">Esquina Juan de Dios Ventura</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                                <RiPhoneLine size={16} className="text-gray-300" />
                                <span className="text-gray-300">*3191 / 1-809-200-3191</span>
                            </div>
                            {/* <div className="flex items-center gap-2">
                                <RiMailLine size={16} className="text-gray-300" />
                                <a 
                                    href="mailto:contacto@poderjudicial.gob.do" 
                                    className="text-gray-300 hover:text-white transition-colors"
                                >
                                    contacto@poderjudicial.gob.do
                                </a>
                            </div> */}
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="pt-4 border-t border-gray-600/30">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-gray-300">
                        <span>© 2025 Poder Judicial de la República Dominicana. Todos los derechos reservados.</span>
                        <span>Código Postal: 10101 | Apartado Postal 1485</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderFull = () => (
        <div className="py-8 px-6">
            <div className="max-w-6xl mx-auto">
                {/* grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">

                    <div className="flex flex-col items-center md:items-start">
                        {showLogo && (
                            <img
                                src={logoBlanco}
                                alt="Poder Judicial"
                                className="h-20 w-auto mb-4"
                            />
                        )}
                        <h3 className="text-white font-semibold text-lg mb-2 text-center md:text-left">
                            Poder Judicial de la República Dominicana
                        </h3>
                        <div className="flex justify-center md:justify-start">
                            <img
                                src={JusticiaAlDiaSvg}
                                alt="Justicia al Día"
                                className="h-12 w-auto"
                            />
                        </div>
                    </div>


                    <div className="text-white">
                        <h4 className="font-semibold text-lg mb-4">Información de contacto</h4>
                        <div className="space-y-3 text-sm">
                            <div className="flex items-start gap-3">
                                <RiMapPinLine size={18} className="mt-0.5 text-gray-300 flex-shrink-0" />
                                <div>
                                    <p>Av. Enrique Jiménez Moya</p>
                                    <p>Esquina Juan de Dios Ventura</p>
                                    <p className="text-gray-300 mt-1">Código Postal: 10101</p>
                                    <p className="text-gray-300">Apartado Postal 1485</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <RiPhoneLine size={18} className="text-gray-300" />
                                <div>
                                    <p>*3191</p>
                                    <p className="text-gray-300">1-809-200-3191 (interior)</p>
                                </div>
                            </div>
                            {/* <div className="flex items-center gap-3">
                                <RiMailLine size={18} className="text-gray-300" />
                                <a 
                                    href="mailto:contacto@poderjudicial.gob.do" 
                                    className="hover:text-gray-300 transition-colors"
                                >
                                    contacto@poderjudicial.gob.do
                                </a>
                            </div> */}
                        </div>
                    </div>


                    <div className="text-white">
                        <h4 className="font-semibold text-lg mb-4">Servicios digitales</h4>
                        <div className="space-y-2 text-sm text-gray-300">
                            <p>Portal de Acceso Digital</p>
                            <p>Consulta de expedientes</p>
                            <p>Gestión de trámites</p>
                            <p>Audiencias virtuales</p>
                        </div>
                    </div>
                </div>

                {/* Copyright */}
                <div className="pt-6 border-t border-gray-600/30">
                    <div className="text-center text-sm text-gray-300">
                        © 2025 Poder Judicial de la República Dominicana. Todos los derechos reservados.
                    </div>
                </div>
            </div>
        </div>
    );

    const getVariantContent = () => {
        switch (variant) {
            case 'minimal':
                return renderMinimal();
            case 'full':
                return renderFull();
            default:
                return renderStandard();
        }
    };

    return (
        <div className="relative">
            <footer className="w-full bg-[var(--color-blue-dark)] text-white">
                {getVariantContent()}
            </footer>

            {/* Scroll button --> top */}
            {/* {showScrollButton && (
                <button
                    onClick={handleScrollToTop}
                    className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-12 h-12 bg-white text-[var(--color-blue-dark)] rounded-full shadow-lg hover:shadow-xl hover:bg-gray-100 transition-all duration-300 border-2 border-[var(--color-blue-dark)]"
                    title="Volver arriba"
                    aria-label="Volver arriba"
                >
                    <RiArrowUpLine size={24} />
                </button>
            )} */}
        </div>
    );
};

export default CompactFooter;